import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { demandAPI } from '../../services/api';
import { ClipboardList, Inbox, CheckSquare, Star } from 'lucide-react';
import { DashboardSkeleton } from '../../components/skeletons';

export default function BuyerHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [demands, setDemands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ active: 0, offers: 0, closed: 0, total: 0 });

  useEffect(() => {
    demandAPI.mine()
      .then(r => {
        const list = r.data.data || r.data || [];
        setDemands(list);
        const active = list.filter(d => d.status === 'active').length;
        const closed = list.filter(d => d.status === 'closed').length;
        const offers = list.reduce((s, d) => s + (d.offersCount || d.offers?.length || 0), 0);
        const total = list.length;
        setStats({ active, offers, closed, total });
      })
      .catch(() => { })
      .finally(() => setLoading(false));
  }, [user]);

  const statCards = [
    { label: 'Active Demands', value: stats.active, icon: <ClipboardList size={28} color="var(--green)" />, color: 'var(--green)' },
    { label: 'Offers Received', value: stats.offers, icon: <Inbox size={28} color="var(--gold)" />, color: 'var(--gold)' },
    { label: 'Deals Closed', value: stats.closed, icon: <CheckSquare size={28} color="var(--blue)" />, color: 'var(--blue)' },
    { label: 'Total Demands', value: stats.total, icon: <ClipboardList size={28} color="var(--purple)" />, color: 'var(--purple)' },
  ];

  if (loading) {
    return <DashboardSkeleton />;
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div className="heading-md">Welcome back, {user?.fullName?.split(' ')[0] || 'Buyer'}!</div>
          <p className="text-muted text-sm" style={styles.headerSubtitle}>Here is your buying activity at a glance.</p>
        </div>
        <button
          className="btn btn-pri btn-md"
          style={styles.postButton}
          onClick={() => navigate('/buyer/post-demand')}
        >
          + Post New Demand
        </button>
      </div>

      {/* Stat Cards */}
      <div style={styles.statsGrid}>
        {statCards.map((s, i) => (
          <div key={i} className="card" style={styles.statCard}>
            <div style={styles.statCardContent}>
              <div>
                <div className="text-muted text-xs" style={styles.statLabel}>{s.label}</div>
                <div style={{ ...styles.statValue, color: s.color }}>{s.value}</div>
              </div>
              <div style={styles.statIcon}>{s.icon}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Demands */}
      <div style={styles.demandsHeader}>
        <div className="heading-sm">Recent Demands</div>
        {demands.length > 0 && (
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/buyer/demands')}>
            View All
          </button>
        )}
      </div>

      {demands.length === 0 ? (
        <div className="card" style={styles.emptyState}>
          <div style={styles.emptyIcon}>
            <ClipboardList size={48} color="var(--green)" style={{ margin: '0 auto 12px', opacity: 0.8 }} />
          </div>
          <div className="heading-sm" style={styles.emptyTitle}>No demands yet</div>
          <p className="text-muted text-sm">Post your first demand and let vendors compete for your business.</p>
          <button
            className="btn btn-pri btn-md"
            style={styles.emptyButton}
            onClick={() => navigate('/buyer/post-demand')}
          >
            Post Your First Demand
          </button>
        </div>
      ) : (
        <div style={styles.demandsList}>
          {demands.slice(0, 5).map(d => (
            <div
              key={d._id}
              className="card"
              style={styles.demandCard}
              onClick={() => navigate(`/buyer/demands/${d._id}`)}
            >
              <div style={styles.demandCardContent}>
                <div style={styles.demandInfo}>
                  <div style={styles.demandTitle}>{d.title}</div>
                  <div className="text-muted text-xs" style={styles.demandTags}>
                    {d.categoryId?.name && <span className="tag tag-green">{d.categoryId.name}</span>}
                    {d.wantsLoan && <span className="tag" style={{ background: '#FFF4D6', color: '#8A5A00' }}>🏦 FINANCING</span>}
                    {!d.nationwide && d.location?.city && <span>{d.location.city}</span>}
                  </div>
                </div>
                <div style={styles.demandMeta}>
                  <div style={styles.demandPrice}>
                    {d.price != null ? `\u20B9${Number(d.price).toLocaleString('en-IN')}` : '--'}
                  </div>
                  <div className="text-muted text-xs">{d.offersCount || d.offers?.length || 0} offers</div>
                </div>
                <span
                  className={`tag ${d.status === 'active' ? 'tag-green' : d.status === 'closed' ? 'tag-blue' : 'tag-gold'}`}
                  style={styles.demandStatus}
                >
                  {d.status?.toUpperCase() || 'ACTIVE'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: 'clamp(12px, 5vw, 24px)',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 'clamp(16px, 4vw, 24px)',
    gap: 'clamp(12px, 3vw, 20px)',
    flexWrap: 'wrap',
    '@media (max-width: 768px)': {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  headerContent: {
    flex: 1,
    minWidth: 0,
  },
  headerSubtitle: {
    marginTop: '4px',
  },
  postButton: {
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 'clamp(12px, 3vw, 16px)',
    marginBottom: 'clamp(20px, 5vw, 28px)',
  },
  statCard: {
    padding: 'clamp(12px, 3vw, 18px) clamp(12px, 3vw, 20px)',
  },
  statCardContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '12px',
  },
  statLabel: {
    marginBottom: '6px',
    display: 'block',
  },
  statValue: {
    fontFamily: 'var(--font-serif)',
    fontSize: 'clamp(24px, 6vw, 28px)',
    fontWeight: 700,
  },
  statIcon: {
    fontSize: 'clamp(24px, 6vw, 28px)',
    opacity: 0.5,
    flexShrink: 0,
  },
  demandsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 'clamp(10px, 3vw, 14px)',
    gap: '12px',
    flexWrap: 'wrap',
  },
  demandsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'clamp(8px, 2vw, 12px)',
  },
  demandCard: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'stretch',
    padding: 'clamp(12px, 3vw, 16px)',
    transition: 'box-shadow 0.2s ease, transform 0.2s ease',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      transform: 'translateY(-2px)',
    },
  },
  demandCardContent: {
    display: 'flex',
    alignItems: 'center',
    gap: 'clamp(12px, 3vw, 16px)',
    width: '100%',
    flexWrap: 'wrap',
  },
  demandInfo: {
    flex: 1,
    minWidth: 'clamp(200px, 50%, 100%)',
  },
  demandTitle: {
    fontSize: 'clamp(13px, 2.5vw, 14px)',
    fontWeight: 700,
    color: 'var(--char)',
    marginBottom: '4px',
    wordBreak: 'break-word',
  },
  demandTags: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  demandMeta: {
    textAlign: 'right',
    minWidth: 'clamp(60px, 15%, 120px)',
    flexShrink: 0,
  },
  demandPrice: {
    fontFamily: 'var(--font-serif)',
    fontWeight: 700,
    color: 'var(--green)',
    fontSize: 'clamp(13px, 2.5vw, 16px)',
    marginBottom: '2px',
  },
  demandStatus: {
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  emptyState: {
    textAlign: 'center',
    padding: 'clamp(32px, 8vw, 48px) clamp(16px, 4vw, 20px)',
  },
  emptyIcon: {
    fontSize: 'clamp(40px, 10vw, 48px)',
    marginBottom: 'clamp(8px, 2vw, 12px)',
  },
  emptyTitle: {
    marginBottom: '6px',
  },
  emptyButton: {
    marginTop: 'clamp(12px, 3vw, 16px)',
  },
};